import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import TanstackTable from '../../Common/TanstackTable/TanstackTable';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import Pagination from '../../Common/DaisyTable/Pagination/Pagination';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import TherapistDetails from './TherapistDetails';

const fetchTherapists = async () => {
    const response = await api.get(API.ADMIN_ALL_THERAPISTS);
    if (response.status === 200) {
        return response.data.values;
    }
    return [];
};

const TherapistsManagement = () => {
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedTherapist, setSelectedTherapist] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sorting, setSorting] = useState([]);

    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });
    const [tableContext, setTableContext] = useState(null);

    // Query for fetching all therapists
    const {
        data,
        error,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['therapists'],
        queryFn: fetchTherapists
    });

    // Filtrer d'abord les données en fonction de la recherche
    const filteredData = useMemo(() => {
        if (!data) return [];

        if (!searchQuery) return data;

        return data.filter(therapist => {
            const searchLower = searchQuery.toLowerCase();
            return (
                (therapist.firstname && therapist.firstname.toLowerCase().includes(searchLower)) ||
                (therapist.lastname && therapist.lastname.toLowerCase().includes(searchLower)) ||
                (therapist.id && therapist.id.toString().includes(searchLower))
            );
        });
    }, [data, searchQuery]);

    // Appliquer le tri aux données filtrées
    const sortedData = useMemo(() => {
        if (!filteredData || !sorting.length) return filteredData;

        const sorted = [...filteredData];

        return sorted.sort((a, b) => {
            for (const sort of sorting) {
                const { id, desc } = sort;

                let valueA, valueB;

                // Gérer les différents types de colonnes
                if (id === 'id' || id === 'currentCredits') {
                    valueA = a[id];
                    valueB = b[id];
                } else if (id === 'firstname' || id === 'lastname') {
                    valueA = a[id]?.toLowerCase() || '';
                    valueB = b[id]?.toLowerCase() || '';
                } else if (id === 'creationDate') {
                    valueA = new Date(a[id]).getTime();
                    valueB = new Date(b[id]).getTime();
                } else {
                    valueA = a[id];
                    valueB = b[id];
                }

                // Comparaison
                if (valueA < valueB) {
                    return desc ? 1 : -1;
                }
                if (valueA > valueB) {
                    return desc ? -1 : 1;
                }
            }
            return 0;
        });
    }, [filteredData, sorting]);

    // Calculer ensuite la page courante à partir des données triées
    const currentPageData = useMemo(() => {
        if (!sortedData) return [];
        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return sortedData.slice(start, end);
    }, [sortedData, pagination]);

    // Calculer le nombre de pages à partir des données triées
    const pageCount = useMemo(() => {
        if (!sortedData) return 0;
        return Math.ceil(sortedData.length / pagination.pageSize);
    }, [sortedData, pagination.pageSize]);

    // Réinitialiser la page à 0 quand le filtre change
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [searchQuery]);

    // Create a mock table object for the Pagination component
    const mockTable = useMemo(() => ({
        getPageCount: () => pageCount,
        setPageIndex: (index) => {
            setPagination(prev => ({ ...prev, pageIndex: index }));
        },
        setPageSize: (size) => setPagination(prev => ({ ...prev, pageSize: size, pageIndex: 0 })),
        nextPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.min(prev.pageIndex + 1, pageCount - 1) })),
        previousPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) })),
        getRowCount: () => sortedData?.length || 0
    }), [pageCount, sortedData]);

    const handleFetchAll = () => {
        console.log("All data is already fetched");
    };

    const handleOpenDetails = (therapist = null) => {
        setSelectedTherapist(therapist);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedTherapist(null);
    };

    const tableRef = useRef(null);

    // Effect to scroll to top when pagination changes
    useEffect(() => {
        if (tableRef.current) {
            const parent = tableRef.current.parentElement;
            parent.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);

    if (isError) {
        return <div className="alert alert-error">Une erreur est survenue : {error.message}</div>;
    }

    if (isLoading) {
        return <LoadingSpinner />;
    }

    const columns = [
        {
            header: 'ID',
            accessorKey: 'id',
            sortingFn: 'basic',
        },
        {
            header: 'Prénom',
            accessorKey: 'firstname',
            sortingFn: 'text',
        },
        {
            header: 'Nom',
            accessorKey: 'lastname',
            sortingFn: 'text',
        },
        {
            header: 'Crédits',
            accessorKey: 'currentCredits',
            sortingFn: 'basic',
        },
        {
            header: 'Date de création',
            accessorKey: 'creationDate',
            cell: ({ row }) => {
                if (!row?.original?.creationDate) return 'Date invalide';
                const date = parseISO(row?.original?.creationDate);
                return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: fr }) : 'Date invalide';
            },
            sortingFn: 'datetime',
        },
        {
            header: 'Actions',
            accessorKey: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetails(row.original);
                    }}
                >
                    Détails
                </button>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion des Thérapeutes</h2>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-outline"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Actualisation...' : 'Actualiser'}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleOpenDetails()}
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Ajouter un thérapeute
                    </button>
                </div>
            </div>

            <div className="flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Rechercher par Id, Prénom ou Nom"
                    className="input input-md input-primary input-bordered w-full max-w-xs"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className='flex flex-row gap-2'>
                    <Pagination
                        server={{
                            total: sortedData?.length || 0,
                            fetched: sortedData?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                        handleFetchAll={handleFetchAll}
                    />

                    <p className="text-xs text-gray-600 self-end">  {sortedData.length} résultats</p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4" ref={tableRef}>
                <TanstackTable
                    data={currentPageData}
                    columns={columns}
                    onRowClick={(row) => handleOpenDetails(row.original)}
                    sorting={sorting}
                    setSorting={setSorting}
                />

                <div className="mt-4 flex justify-end">
                    <Pagination
                        server={{
                            total: sortedData?.length || 0,
                            fetched: sortedData?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                        handleFetchAll={handleFetchAll}
                    />
                </div>
            </div>

            <TherapistDetails
                isOpen={isDetailsModalOpen}
                onClose={handleCloseDetails}
                therapist={selectedTherapist}
            />
        </div>
    );
};

export default TherapistsManagement; 